// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Rectangle } from '@shared/commons/Geometry';
import { Cursors } from '@shared/commons/Cursors';
import { ProMath } from '../ToolView';
import { AlertUtils } from '@shared/utils/Alert/AlertUtils';
import { TerceraChartTradingOperation } from '../../Utils/ChartConstants';
import { TerceraChartHistoryType } from '../../TerceraChartUtils';
import { TradingToolViewBase, TradingToolViewType } from './TradingToolViewBase';
import { SelectionState, ToolSelectionType } from '../Selection';
import { AlertCondition, AlertImportance } from '@shared/utils/Alert/AlertConstants';
import { RectangleEdgeFilter } from '@shared/commons/Graphics';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { LayersEnum } from '../../Utils/LayersEnum';
export class AlertToolView extends TradingToolViewBase {
    constructor(alert, getToolViewByAlertId, terceraChartPanelContext, renderer) {
        super();
        this.Alert = alert;
        this.GetToolViewByAlertId = getToolViewByAlertId;
        this.terceraChartPanelContext = terceraChartPanelContext;
        this.myRenderer = renderer;
        this.ToolViewType = TradingToolViewType.AlertToolView;
        this.Collapsed = true;
        this.Updated(alert);
    }
    get Collapsed() {
        return this.collapsed;
    }
    set Collapsed(value) {
        this.collapsed = value;
    }
    get ID() {
        return this.Alert.AlertId;
    }
    get Price() {
        return this.Alert.Value;
    }
    Dispose() {
        this.Alert = null;
        this.GetToolViewByAlertId = null;
        super.Dispose();
    }
    Draw(gr, window, param) {
        this.MainOrderVisibility = false;
        if (isNullOrUndefined(this.myRenderer) || !this.myRenderer.ShowAlerts || !this.Alert) {
            return;
        }
        const CR = window.ClientRectangle;
        const minX = CR.X;
        const maxX = minX + CR.Width;
        try {
            let screenPoints00 = this.screenPoints[0][0];
            if (screenPoints00 < minX) {
                screenPoints00 = minX;
            }
            screenPoints00 = minX;
            const screenPoints01 = this.screenPoints[0][1];
            // Ловим ошибки
            if ((screenPoints00 < 0 && maxX < 0) ||
                screenPoints01 >= ProMath.infinity ||
                screenPoints01 >= ProMath.infinity ||
                screenPoints01 <= ProMath.infinityMinus ||
                screenPoints01 <= ProMath.infinityMinus) {
                return;
            }
            // оптимизация - вне не рисуем ордер и не добавляем его плашку
            if (!CR.Contains(screenPoints00, screenPoints01)) {
                return;
            }
            const separators = [];
            this.MainOrderVisibility = true;
            let curX = screenPoints00;
            const curY = screenPoints01 - this.containerHeight / 2;
            this.imageRect = new Rectangle(curX, curY, this.btnSize, this.btnSize);
            curX += this.imageRect.Width;
            if (!this.Collapsed) {
                curX = this.addRectWithSeparator(curX, curY, this.btnSize, separators, (r) => {
                    this.actionButton = r;
                });
                curX = this.addRectWithSeparator(curX, curY, this.btnSize, separators, (r) => {
                    this.removeButton = r;
                });
            }
            else {
                this.actionButton = Rectangle.Empty();
                this.removeButton = Rectangle.Empty();
            }
            this.mainContainer = new Rectangle(screenPoints00, curY, curX - screenPoints00, this.containerHeight);
            const hoveredOrSelected = this.CurrentSelection.CurrentState !== SelectionState.None;
            const { backgroundBrush, hoveredBrush, priceLinePen, pricePlateBrush } = this.getDrawingTools(hoveredOrSelected);
            gr.RoundRectangle(this.mainContainer, 4, backgroundBrush);
            const alertImg = this.CurrentSelection.CurrentHover !== ToolSelectionType.None
                ? ThemeManager.NewImages.alertIconHovered
                : ThemeManager.NewImages.alertIconDefault;
            gr.DrawCenteredImage(alertImg.image, this.imageRect);
            const getBtnBrush = (toolType) => this.CurrentSelection.CurrentHover === toolType ? hoveredBrush : backgroundBrush;
            this.drawImgButton(gr, this.actionButton, ThemeManager.NewImages.alertEditDefault, ThemeManager.NewImages.alertEditHovered, getBtnBrush(ToolSelectionType.Action));
            this.drawImgButton(gr, this.removeButton, ThemeManager.NewImages.closeIconDefault, ThemeManager.NewImages.closeIconHovered, getBtnBrush(ToolSelectionType.Remove), RectangleEdgeFilter.TopRight | RectangleEdgeFilter.BottomRight);
            this.drawSeparators(gr, separators);
            this.drawPriceLine(gr, this.mainContainer, maxX, screenPoints01, priceLinePen);
            this.tryAddPlateOnPriceScale(param, this.priceValue, this.Price, pricePlateBrush);
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log(ex);
        }
    }
    Updated(newAlert, isAlertDataUpdate) {
        if (this.Alert === null || !newAlert) {
            return;
        }
        this.Alert = newAlert;
        this.dataCacheTool.Points[0][1] = this.Price;
        if (!this.CurrentMovement.IsMovingRightNow && !isAlertDataUpdate) {
            this.CancelMoving();
        }
        this.myRenderer.chart.IsDirty(LayersEnum.Tools);
    }
    GetCurrentSelection(x, y) {
        let newSelection = ToolSelectionType.None;
        if (this.imageRect.Contains(x, y)) {
            newSelection = ToolSelectionType.Image;
            this.Collapsed = false;
        }
        else if (this.actionButton.Contains(x, y)) {
            newSelection = ToolSelectionType.Action;
        }
        else if (this.removeButton.Contains(x, y)) {
            newSelection = ToolSelectionType.Remove;
        }
        else if (this.mainContainer.Contains(x, y)) {
            newSelection = ToolSelectionType.Container;
        }
        else {
            this.Collapsed = true;
        }
        return newSelection;
    }
    ProcessClick() {
        if (this.CurrentSelection.MouseDownHover === this.CurrentSelection.CurrentHover) {
            // click
            switch (this.CurrentSelection.MouseDownHover) {
                case ToolSelectionType.Action:
                    this.terceraChartPanelContext.ChartVisualTrading(this.Alert, {
                        action: TerceraChartTradingOperation.EditAlert
                    });
                    break;
                case ToolSelectionType.Remove:
                    this.terceraChartPanelContext.ChartVisualTrading(this.Alert, {
                        action: TerceraChartTradingOperation.RemoveAlert
                    });
                    break;
                default:
                    break;
            }
        }
        this.ResetMouse();
    }
    GetCursor(e) {
        const curElement = this.CurrentSelection.CurrentHover;
        switch (curElement) {
            case ToolSelectionType.Image:
                return Cursors.NoMoveVert;
            case ToolSelectionType.Action:
            case ToolSelectionType.Remove:
                return Cursors.Default;
            default:
                return null;
        }
    }
    IsSelectCheck(x, y) {
        if (this.TradeWasModified) {
            // тулза смещена и с сервера не пришло подтверждение не двигаем её
            return false;
        }
        const newSelection = this.GetCurrentSelection(x, y);
        return newSelection !== ToolSelectionType.None;
    }
    CheckMinDistanceForStartMoving(x, y) {
        const mouseDownHover = this.CurrentSelection.MouseDownHover;
        if (mouseDownHover === ToolSelectionType.Image) {
            return Math.abs(this.CurrentMovement.PrevY - y) >= this.MinDistanceForStartMoving();
        }
        return false;
    }
    // start move
    ProcessMoveStart(x, y) {
        super.ProcessMoveStart(x, y);
    }
    // move
    ProcessNewPosition(window, x, y, cashItemSeries = null) {
        this.TradeWasModified = true;
        // this.errorMode = false;
        let data = 0;
        data = window.PointsConverter.GetDataY(y);
        if (!isNullOrUndefined(cashItemSeries)) {
            data = cashItemSeries.settings.RevertConverter(window.PointsConverter.GetDataY(y));
        }
        // move alert
        super.ProcessNewPosition(window, x, y, cashItemSeries);
        this.priceValue = data;
    }
    // mouse finish
    ProcessMoveFinish() {
        try {
            super.ProcessMoveFinish();
            const mouseDownHover = this.CurrentSelection.MouseDownHover;
            if (mouseDownHover === ToolSelectionType.Image) {
                const newPrice = this.myRenderer.Instrument !== null
                    ? this.myRenderer.Instrument.roundPrice(this.priceValue)
                    : this.priceValue;
                const ht = TerceraChartHistoryType.GetOriginalHistoryType(this.myRenderer.chart.ChartHistoryType(), this.myRenderer.Instrument);
                const valueFromInstrument = AlertUtils.GetCorrectPriceForAlertByInstrument(this.myRenderer.Instrument, ht);
                let condition = null;
                if (!isNaN(valueFromInstrument)) {
                    condition = newPrice > valueFromInstrument ? AlertCondition.MORE_EQUAL : AlertCondition.LESS_EQUAL;
                }
                this.lastValidPriceValue = newPrice;
                this.terceraChartPanelContext.ChartVisualTrading(this.Alert, {
                    Value: newPrice,
                    Condition: condition,
                    MoveChart: true,
                    action: TerceraChartTradingOperation.AlertReplacement
                });
            }
            else {
                super.ProcessMoveFinish();
            }
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log(ex);
            this.ResetMouse();
            super.ProcessMoveFinish();
        }
    }
    FinishMove() {
        this.TradeWasModified = false;
        super.ProcessMoveFinish();
        this.ResetMove();
        this.MouseDownHover = ToolSelectionType.None;
    }
    ResetMove() {
        this.lastValidPriceValue = NaN;
        this.ResetMouse();
    }
    ResetMouse() {
        this.CurrentSelection.MouseDownHover = ToolSelectionType.None;
        this.priceValue = NaN;
    }
    CancelMove() {
        this.FinishMove();
        this.Updated();
    }
    CancelMoving() {
        this.ResetMouse();
        this.TradeWasModified = false;
    }
    getDrawingTools(hoveredOrSelected) {
        let backgroundBrush;
        let hoveredBrush;
        let priceLinePen;
        let pricePlateBrush;
        if (this.TradeWasModified) {
            backgroundBrush = TradingToolViewBase.chartOrderModifiedBrush;
            hoveredBrush = TradingToolViewBase.chartOrderModifiedBrush;
            priceLinePen = TradingToolViewBase.chartOrderModifiedPen;
            pricePlateBrush = TradingToolViewBase.chartOrderModifiedBrush;
        }
        else if (this.Alert.Importance === AlertImportance.HIGH) {
            backgroundBrush = hoveredOrSelected
                ? TradingToolViewBase.highAlertBackgroundBrush
                : TradingToolViewBase.highAlertBackgroundInactiveBrush;
            hoveredBrush = TradingToolViewBase.highAlerHoveredBrush;
            priceLinePen = this.myRenderer.highAlertPricePen;
            pricePlateBrush = TradingToolViewBase.highAlertBackgroundBrush;
        }
        else if (this.Alert.Importance === AlertImportance.MEDIUM) {
            backgroundBrush = hoveredOrSelected
                ? TradingToolViewBase.mediumAlertBackgroundBrush
                : TradingToolViewBase.mediumAlertInactiveBackgroundBrush;
            hoveredBrush = TradingToolViewBase.mediumAlertHoveredBrush;
            priceLinePen = this.myRenderer.mediumAlertPricePen;
            pricePlateBrush = TradingToolViewBase.mediumAlertBackgroundBrush;
        }
        else if (this.Alert.Importance === AlertImportance.LOW) {
            backgroundBrush = hoveredOrSelected
                ? TradingToolViewBase.lowAlertBackgroundBrush
                : TradingToolViewBase.lowAlertInactiveBackgroundBrush;
            hoveredBrush = TradingToolViewBase.lowAlertHoveredBrush;
            priceLinePen = this.myRenderer.lowAlertPricePen;
            pricePlateBrush = TradingToolViewBase.lowAlertBackgroundBrush;
        }
        return { backgroundBrush, hoveredBrush, priceLinePen, pricePlateBrush };
    }
}
